import api from "@/api";
import deepClone from "@/utils/deep-clone";

const state = {
    categories: [],
    skills: [],
    matrix: [],
    customRoles: [],
    listByUser: {},
    details: {},
};

const getters = {
    getMaxOrder: state => {
        if (state.skills && state.skills.length) {
            return Math.max.apply(
                Math,
                state.skills.map(s => s.order),
            );
        }
        return 0;
    },
    getSkillById: state => id => {
        if (state.details[id]) {
            return deepClone(state.details[id]);
        }
        return deepClone(state.skills.find(s => s._id === id) ?? {});
    },
    getSkills(state) {
        return () => deepClone(state.skills ?? []);
    },
    getMatrix(state) {
        return () => deepClone(state.matrix ?? []);
    },
    getCustomRoles(state) {
        return () => deepClone(state.customRoles ?? []);
    },
    getCategories(state) {
        return deepClone(state.categories ?? {});
    },
    getCategoryDetails: state => id => {
        return deepClone(state.categories.find(c => c._id === id) ?? {});
    },
    getSkillsByUser(state) {
        return id => state.listByUser[id] ?? [];
    },
};

const actions = {
    getSkills({ commit, rootGetters }, { payload: { showAll } }) {
        const user = rootGetters["users/current/user"];

        if (!user) {
            return;
        }

        return api.get(`/api/skills/get/skills/list/${user._id}/showall/${showAll}`).then(res => {
            const skills = res?.data?.skills ?? [];
            commit("updateSkills", skills);
        });
    },
    getSkillDetails({ commit }, { payload: { id } }) {
        return api.get(`/api/skills/get/details/skill/${id}`).then(res => {
            if (!res.data) {
                return;
            }

            commit("updateDetails", res.data);
        });
    },
    getCategories({ commit }) {
        return api.get(`/api/skills/category/list/skills`).then(res => {
            const data = res?.data ?? [];

            commit("updateCategories", data);
        });
    },
    getMatrix({ commit }, { payload: { matrixMode } }) {
        return api.get(`/api/skills/get/matrix/${matrixMode}`).then(res => {
            const matrix = res?.data?.matrix ?? [];
            const customRoles = res?.data?.customRoles ?? [];

            commit("updateMatrix", { matrix, customRoles });
        });
    },
    getListForUser({ commit }, { payload: { showAll = false, userId } }) {
        if (!userId) {
            return;
        }

        return api.get(`/api/skills/get/skills/list/${userId}/showall/${showAll}`).then(res => {
            const skills = res?.data?.skills ?? [];

            commit("updateListByUser", { userId, skills });
        });
    },
    updateOrder({ commit, dispatch }, { payload: { skills } }) {
        if (!skills.length) {
            return dispatch("getList");
        }

        return api.post(`/api/skills/update/order`, skills).then(res => {
            const data = res?.data;
            if (data === "done") {
                commit("updateSkills", skills);
            }
        });
    },
    createCategory: ({ commit }, { payload: { name } }) => {
        return api.post(`/api/skills/category`, { name, type: "skills" }).then(res => {
            const data = res?.data ?? [];
            commit("updateCategories", data);
        });
    },
    updateCategory: ({ commit }, { payload: { name, categoryId } }) => {
        return api.put(`/api/skills/category/${categoryId}`, { name, type: "skills" }).then(res => {
            const data = res?.data ?? [];
            commit("updateCategories", data);
        });
    },
    deleteCategory: ({ commit }, { payload: { categoryId } }) => {
        return api.delete(`/api/skills/category/${categoryId}`).then(res => {
            const data = res?.data ?? [];
            commit("updateCategories", data);
        });
    },
    updateSkill({ commit, state }, { skill }) {
        return api.post(`/api/skills/create`, skill).then(res => {
            if (res?.data) {
                const skills = deepClone(state.skills) ?? [];
                const index = skills.map(s => s._id).indexOf(skill._id);
                if (index === -1) {
                    skills.push(res.data);
                } else {
                    skills[index] = res.data;
                }
                commit("updateSkills", skills);
            }
            // TODO error case
        });
    },
    deleteSkill({ commit, state }, { id }) {
        return api.get(`/api/skills/delete/skill/${id}`).then(res => {
            if (res?.data) {
                commit(
                    "updateSkills",
                    state.skills.filter(s => s._id !== id),
                );
            }
            // TODO error case
        });
    },
};

const mutations = {
    updateSkills(state, skills) {
        state.skills = [...skills];
    },
    updateDetails(state, skill) {
        state.details[skill._id] = { ...skill };
    },
    updateMatrix(state, { matrix, customRoles }) {
        state.matrix = [...matrix];
        state.customRoles = [...customRoles];
    },
    updateCategories(state, data) {
        state.categories = data;
    },
    updateListByUser(state, { userId, skills }) {
        state.listByUser[userId] = [...skills];
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
